"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, animate } from "framer-motion";
import { ChevronRight, Mail, ChevronDown } from "lucide-react";
import { SiteNavbar } from "@/shared/ui/site-navbar";
import { SiteFooter } from "@/shared/ui/site-footer";
import { HomeCtaSection } from "@/features/home/presentation/components/home-cta-section";
import type { HomePageContent } from "@/features/home/types/home-page-content";
import { FAQ_SECTIONS } from "../../constants/faq-sections";

/* ------------------------------------------------------------------ */
/*  Accordion Item                                                     */
/* ------------------------------------------------------------------ */

interface FaqAccordionItemProps {
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  question: string;
  tags?: string[];
}
function FaqAccordionItem(t0) {
  const $ = _c(28);
  const {
    answer,
    isOpen,
    onToggle,
    question,
    tags
  } = t0;
  const t1 = `mb-4 flex flex-col overflow-hidden rounded-xl border transition-all duration-300 ${isOpen ? "border-primary/20 bg-card shadow-[0_8px_30px_rgba(21,90,125,0.04)]" : "border-border bg-card shadow-[0_2px_8px_rgba(0,0,0,0.01)] hover:border-primary/30 hover:shadow-sm"}`;
  let t2;
  if ($[0] !== question) {
    t2 = <span className="text-[16px] font-bold text-foreground sm:text-[18px]">{question}</span>;
    $[0] = question;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-all duration-300 ${isOpen ? "bg-primary text-white shadow-md shadow-primary/10" : "border border-border bg-card hover:bg-muted"}`;
  const t4 = isOpen ? 180 : 0;
  let t5;
  if ($[2] !== t4) {
    t5 = {
      rotate: t4
    };
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      duration: 0.3,
      ease: "easeInOut"
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = `h-4.5 w-4.5 ${isOpen ? "text-white" : "text-muted-foreground"}`;
  let t8;
  if ($[5] !== t7) {
    t8 = <ChevronDown className={t7} strokeWidth={2.5} />;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== t5 || $[8] !== t8) {
    t9 = <motion.div animate={t5} transition={t6}>{t8}</motion.div>;
    $[7] = t5;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t3 || $[11] !== t9) {
    t10 = <div className={t3}>{t9}</div>;
    $[10] = t3;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== isOpen || $[14] !== onToggle || $[15] !== t10 || $[16] !== t2) {
    t11 = <button className="group flex w-full items-center justify-between gap-4 p-6 text-left outline-none focus-visible:ring-2 focus-visible:ring-accent/40 sm:p-8" onClick={onToggle} type="button" aria-expanded={isOpen}>{t2}{t10}</button>;
    $[13] = isOpen;
    $[14] = onToggle;
    $[15] = t10;
    $[16] = t2;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== answer || $[19] !== isOpen || $[20] !== tags) {
    t12 = isOpen && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: "auto",
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.3,
      ease: [0.25, 0.8, 0.25, 1]
    }} className="overflow-hidden"><div className="flex flex-col gap-4 px-6 pt-0 pb-8 sm:px-8 sm:pb-8"><div className="mt-[-10px] mb-2 h-px w-full bg-border" /><p className="text-[15px] leading-[1.7] text-muted-foreground sm:text-[16px]">{answer}</p>{tags && tags.length > 0 && <div className="flex flex-wrap gap-2 pt-2">{tags.map(_temp)}</div>}</div></motion.div>;
    $[18] = answer;
    $[19] = isOpen;
    $[20] = tags;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12) {
    t13 = <AnimatePresence initial={false}>{t12}</AnimatePresence>;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t1 || $[25] !== t11 || $[26] !== t13) {
    t14 = <div className={t1}>{t11}{t13}</div>;
    $[24] = t1;
    $[25] = t11;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
}

/* ------------------------------------------------------------------ */
/*  Sidebar Nav Link                                                   */
/* ------------------------------------------------------------------ */
function _temp(tag) {
  return <span key={tag} className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3.5 py-1 text-[12px] font-semibold text-primary">{tag}</span>;
}
function SidebarLink(t0) {
  const $ = _c(7);
  const {
    isActive,
    label,
    onClick
  } = t0;
  const t1 = `group flex w-full items-center gap-2.5 rounded-lg px-4 py-2.5 text-left text-[14px] font-medium transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-accent/20 ${isActive ? "bg-primary/[0.06] font-semibold text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`;
  const t2 = `size-3.5 transition-all duration-200 ${isActive ? "translate-x-0 text-primary opacity-100" : "-translate-x-1 text-muted-foreground opacity-0 group-hover:translate-x-0 group-hover:opacity-60"}`;
  let t3;
  if ($[0] !== t2) {
    t3 = <ChevronRight className={t2} />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== label || $[3] !== onClick || $[4] !== t1 || $[5] !== t3) {
    t4 = <button className={t1} onClick={onClick} type="button">{t3}{label}</button>;
    $[2] = label;
    $[3] = onClick;
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}

/* ------------------------------------------------------------------ */
/*  Mobile Section Nav                                                 */
/* ------------------------------------------------------------------ */

function MobileSectionNav(t0) {
  const $ = _c(5);
  const {
    activeSection,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== activeSection || $[1] !== onSelect) {
    t1 = FAQ_SECTIONS.map(section => <button className={`rounded-full px-4 py-2 text-[13px] font-medium whitespace-nowrap transition-all duration-200 ${activeSection === section.id ? "bg-primary text-white shadow-sm" : "border border-border bg-card text-muted-foreground hover:border-primary/30"}`} key={section.id} onClick={() => onSelect(section.id)} type="button">{section.label}</button>);
    $[0] = activeSection;
    $[1] = onSelect;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="scrollbar-none flex gap-2 overflow-x-auto pb-1 lg:hidden">{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/* ------------------------------------------------------------------ */
/*  FAQ Page                                                           */
/* ------------------------------------------------------------------ */

interface FaqPageProps {
  content: HomePageContent;
}
export function FaqPage(t0) {
  const $ = _c(42);
  const {
    content
  } = t0;
  const [activeSection, setActiveSection] = useState(FAQ_SECTIONS[0].id);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [openItems, setOpenItems] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const sectionRefs = useRef(t2);
  const isScrollingRef = useRef(false);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = sectionId => {
      const el = sectionRefs.current[sectionId];
      if (!el) {
        return;
      }
      isScrollingRef.current = true;
      setActiveSection(sectionId);
      const elementPosition = el.getBoundingClientRect().top + window.scrollY;
      animate(window.scrollY, elementPosition - 100, {
        type: "tween",
        ease: "easeInOut",
        duration: 0.7,
        onUpdate: _temp2,
        onComplete: () => {
          isScrollingRef.current = false;
        }
      });
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const scrollToSection = t3;
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const handleScroll = function handleScroll() {
        if (isScrollingRef.current) {
          return;
        }
        const halfWindowHeight = window.innerHeight / 2;
        for (let i = FAQ_SECTIONS.length - 1; i >= 0; i--) {
          const section = FAQ_SECTIONS[i];
          const el_0 = sectionRefs.current[section.id];
          if (el_0) {
            const rect = el_0.getBoundingClientRect();
            if (rect.top <= halfWindowHeight) {
              setActiveSection(section.id);
              break;
            }
          }
        }
      };
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", handleScroll);
    };
    t5 = [];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function handleToggle(sectionId_0, index) {
      setOpenItems(prev => ({
        ...prev,
        [sectionId_0]: prev[sectionId_0] === index ? null : index
      }));
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const handleToggle = t6;
  let t7;
  if ($[6] !== content.navigation) {
    t7 = <SiteNavbar content={content.navigation} />;
    $[6] = content.navigation;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="hidden h-full w-full grid-cols-12 divide-x divide-white/15 md:grid"><div className="col-span-1 h-full" /><div className="col-span-3 h-full" /><div className="col-span-4 h-full" /><div className="col-span-3 h-full" /><div className="col-span-1 h-full" /></div>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="pointer-events-none absolute inset-0 z-10 size-full">{t8}<div className="grid h-full w-full grid-cols-6 divide-x divide-white/10 md:hidden"><div className="col-span-1 h-full" /><div className="col-span-1 h-full" /><div className="col-span-2 h-full" /><div className="col-span-1 h-full" /><div className="col-span-1 h-full" /></div></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="absolute inset-0 bg-cover bg-center" style={{
      backgroundImage: "url(https://res.cloudinary.com/dsalsyu0z/image/upload/v1779570947/lauren-mancke-rm5DbquteoY-unsplash_fagt8q.jpg)"
    }}><div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(4,12,14,0.35)_0%,rgba(4,12,14,0.58)_38%,rgba(4,12,14,0.72)_100%)]" /><div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(21,90,125,0.5),transparent_32%)]" /></div>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <motion.p className="mb-6 rounded-full border border-white/20 bg-white/8 px-4 py-2 text-xs font-medium tracking-[0.32em] text-white/80 uppercase backdrop-blur-sm" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }}>Help Center</motion.p>;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <motion.h1 className="max-w-7xl font-heading text-5xl font-normal tracking-tight text-balance text-white md:text-6xl lg:text-[80px]" initial={{
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.08
    }}>Frequently asked questions</motion.h1>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <section className="relative flex min-h-[60svh] items-center justify-center overflow-hidden bg-[#122023]">{t9}{t10}<div className="relative z-20 mx-auto flex w-full max-w-6xl flex-col items-center px-6 py-16 text-center text-white sm:px-10 lg:px-12">{t11}{t12}<motion.p className="mx-auto mt-6 max-w-2xl text-base font-light text-pretty text-white/75 md:text-lg lg:text-xl" initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.16
        }}>Everything you need to know about therapy, healer matching, privacy, and your healing journey.</motion.p></div></section>;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== activeSection) {
    t14 = <div className="sticky top-16 z-40 border-b border-border bg-card/90 px-5 py-3 backdrop-blur-md sm:top-[72px] lg:hidden"><MobileSectionNav activeSection={activeSection} onSelect={scrollToSection} /></div>;
    $[14] = activeSection;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== activeSection) {
    t15 = FAQ_SECTIONS.map(section_0 => <SidebarLink isActive={activeSection === section_0.id} key={section_0.id} label={section_0.label} onClick={() => scrollToSection(section_0.id)} />);
    $[16] = activeSection;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== t15) {
    t16 = <nav className="flex flex-col gap-1">{t15}</nav>;
    $[18] = t15;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  let t18;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <p className="text-[13px] font-semibold text-foreground">Can't find what you need?</p>;
    t18 = <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">Our support team is here to help you.</p>;
    $[20] = t17;
    $[21] = t18;
  } else {
    t17 = $[20];
    t18 = $[21];
  }
  let t19;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="mt-10 rounded-2xl bg-gradient-to-br from-primary/[0.04] to-accent/[0.04] p-5">{t17}{t18}<a className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-[12px] font-semibold text-white transition-all hover:bg-primary/90" href="mailto:support@itshealer.com"><Mail className="size-3.5" />Contact us</a></div>;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] !== t16) {
    t20 = <aside className="hidden lg:block"><div className="sticky top-[120px] w-[220px] shrink-0 py-12">{t16}{t19}</div></aside>;
    $[23] = t16;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== openItems) {
    t21 = FAQ_SECTIONS.map((section_1, idx) => <div key={section_1.id} ref={el_1 => {
      sectionRefs.current[section_1.id] = el_1;
    }} className={`scroll-mt-[140px] lg:scroll-mt-[120px] ${idx === 0 ? "mt-0" : "mt-20 lg:mt-28"}`}><div className="mb-5 lg:mb-6"><h2 className="text-[26px] font-bold tracking-tight text-foreground sm:text-[32px]">{section_1.label}</h2></div><div>{section_1.items.map((item, index_0) => <FaqAccordionItem answer={item.answer} isOpen={openItems[section_1.id] === index_0} key={item.question} onToggle={() => handleToggle(section_1.id, index_0)} question={item.question} tags={item.tags} />)}</div></div>);
    $[25] = openItems;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  let t22;
  if ($[27] !== t21) {
    t22 = <main className="min-w-0 flex-1 py-8 lg:border-l lg:border-border lg:py-12 lg:pl-16">{t21}</main>;
    $[27] = t21;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] !== t20 || $[30] !== t22) {
    t23 = <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12"><div className="relative flex gap-0 lg:gap-16">{t20}{t22}</div></div>;
    $[29] = t20;
    $[30] = t22;
    $[31] = t23;
  } else {
    t23 = $[31];
  }
  let t24;
  if ($[32] !== content.ctaSection) {
    t24 = <div className="bg-card"><HomeCtaSection content={content.ctaSection} /></div>;
    $[32] = content.ctaSection;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  if ($[34] !== content.footer) {
    t25 = <div className="bg-[#0b1416]"><SiteFooter content={content.footer} /></div>;
    $[34] = content.footer;
    $[35] = t25;
  } else {
    t25 = $[35];
  }
  let t26;
  if ($[36] !== t14 || $[37] !== t23 || $[38] !== t24 || $[39] !== t25 || $[40] !== t7) {
    t26 = <div className="relative min-h-screen bg-background">{t7}{t13}{t14}{t23}{t24}{t25}</div>;
    $[36] = t14;
    $[37] = t23;
    $[38] = t24;
    $[39] = t25;
    $[40] = t7;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  return t26;
}
function _temp2(latest) {
  return window.scrollTo(0, latest);
}